import { Textarea as MantineTextarea, TextareaProps } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ThemedTextareaProps extends TextareaProps {
  gradientFocus?: boolean;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'info' | 'warning' | 'danger';
  useColorScheme?: boolean;
  'data-color'?: string;
  'data-accent'?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, ThemedTextareaProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, styles, ...rest }, ref) => {
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);
    const scheme = usePreferencesStore.getState().getColorScheme?.() ?? null;
    const color =
      (useColorScheme && scheme?.primary?.from) ||
      (rest?.['data-color'] as string) ||
      (rest?.['data-accent'] as string) ||
      defaultColorScheme.primary.from;

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (!gradientFocus) {
      return (
        <MantineTextarea
          ref={ref}
          styles={{
            ...styles,
            label: {
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
            input: {
              ...accessibilityStyles,
              ...(styles as any)?.input,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineTextarea
        ref={ref}
        styles={{
          ...styles,
          label: {
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
          input: {
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:focus': {
              borderColor: `${color} !important`,
              boxShadow: strongFocus ? `0 0 0 3px ${color}50 !important` : `0 0 0 2px ${color}30 !important`,
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: `${color} !important`,
            },
            ...accessibilityStyles,
            ...(styles as any)?.input,
          },
        }}
        {...rest}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
